import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        // `positive` mirrors vNext's positive button; vNext's `negative` == our `destructive`.
        positive: 'bg-success text-white hover:bg-success/90',
        outline:
          'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-link underline underline-offset-2 hover:text-info',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-7 gap-1.5 px-3 has-[>svg]:px-2.5',
        xs: 'h-7 gap-1 px-2.5 text-xs has-[>svg]:px-2',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-xs': 'size-7',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonBaseProps = VariantProps<typeof buttonVariants> & {
  loading?: boolean;
  icon?: LucideIcon;
  iconClassName?: string;
  disabled?: boolean;
  className?: string;
};

type ButtonAsChildProps = ButtonBaseProps & {
  asChild: true;
  children: React.ReactElement;
} & Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'className'>;

type ButtonDefaultProps = ButtonBaseProps & {
  asChild?: false;
} & Omit<React.ComponentProps<'button'>, keyof ButtonBaseProps>;

type ButtonProps = ButtonAsChildProps | ButtonDefaultProps;

function Button(props: ButtonProps) {
  const {
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    loading = false,
    icon: Icon,
    iconClassName = 'size-4',
    children,
    disabled,
    ...rest
  } = props;

  // When asChild is true, Slot requires exactly one child element
  // So we skip icon rendering and just pass through children
  if (asChild) {
    return (
      <Slot
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...(rest as React.HTMLAttributes<HTMLElement>)}
      >
        {children as React.ReactElement}
      </Slot>
    );
  }

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={loading || disabled}
      {...(rest as React.ComponentProps<'button'>)}
    >
      {Icon &&
        (loading ? <Spinner className={iconClassName} /> : <Icon className={iconClassName} />)}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
export type { ButtonProps };
